import api from 'store/actions/api';
import { change as changeForm, getFormValues } from 'redux-form';
import {
  concat,
  get,
  trim,
  find,
  flow,
  map,
  toLower,
  toPairs,
} from 'lodash/fp';
import { SEARCH_ADS_FORM } from 'components/searchAds/SearchLayout/constants';
import { VEHICLE_NAME } from 'components/searchAds/constants';
import { selectManufacturerOptions } from 'store/ducks/manufacturers';
import searchActions from 'pages/Search/actions';
import { applyFilters } from 'components/searchAds/SearchLayout/actions';
import { fuzzySearchDuck, REDUCER_KEY as FUZZY_SEARCH_KEY } from '../duck';

const getMake = get([FUZZY_SEARCH_KEY, 'make']);
const getModel = get([FUZZY_SEARCH_KEY, 'model']);
const getSuggestions = get([FUZZY_SEARCH_KEY, 'suggestions']);

const MAKE_SUGGESTIONS_RESET_THRESHOLD = 3;

const searchByLabel = (list, query) => {
  return list.find(
    (listItem) => toLower(listItem.label) === toLower(trim(query)),
  );
};

const getModelFromQuery = (suggestions, query) => {
  if (!find(['isModel', true], suggestions)) {
    return {};
  }

  return searchByLabel(suggestions, query);
};

const updateSearchForm = (make, model, subType) =>
  function* ({ getState }) {
    yield changeForm(SEARCH_ADS_FORM, VEHICLE_NAME, [{ make, model, subType }]);
    const form = getFormValues(SEARCH_ADS_FORM)(getState());

    yield applyFilters({
      changeFilters: searchActions.changeFilters,
      form,
      searchWithFilters: false,
    });
  };

const loadModelsForSuggestions = (make, searchQuery, makeOptions) =>
  function* () {
    yield fuzzySearchDuck.set({ make });

    const {
      result: { wkda: models },
    } = yield api.getCarMainTypes({
      manufacturer: make,
    });

    const makeLabel = flow(get(['label']))(
      searchByLabel(makeOptions, searchQuery),
    );

    yield fuzzySearchDuck.set({ makeLabel });

    const suggestions = flow(
      toPairs,
      map(([value, label]) => ({
        label: `${makeLabel} ${label}`,
        value,
        isModel: true,
      })),
      concat({
        label: makeLabel,
        value: make,
      }),
    )(models);

    yield fuzzySearchDuck.set({ suggestions });
  };

export const searchQueryChanged = (searchQuery) =>
  function* ({ getState }) {
    const makeOptions = selectManufacturerOptions(getState());

    if (!searchQuery || searchQuery.length < MAKE_SUGGESTIONS_RESET_THRESHOLD) {
      yield fuzzySearchDuck.set({ suggestions: makeOptions });
    }

    const make = get(['value'], searchByLabel(makeOptions, searchQuery));

    if (make) {
      yield loadModelsForSuggestions(make, searchQuery, makeOptions);
    }
  };

export const searchQuerySelected = (searchQuery) =>
  function* ({ getState }) {
    const makeOptions = selectManufacturerOptions(getState());

    let selectedMake = get(['value'], searchByLabel(makeOptions, searchQuery));

    const suggestions = getSuggestions(getState());

    let selectedModel = get(
      ['value'],
      getModelFromQuery(suggestions, searchQuery),
    );

    if (selectedModel) {
      yield fuzzySearchDuck.set({ model: selectedModel });
    }

    if (selectedMake) {
      yield updateSearchForm(selectedMake);
      yield fuzzySearchDuck.set({ make: selectedMake });

      return;
    }

    selectedMake = getMake(getState());
    yield fuzzySearchDuck.set({ make: selectedMake });

    selectedModel = selectedModel || getModel(getState());

    yield updateSearchForm(selectedMake, selectedModel);
  };
